import { Form, Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Accordion } from '../../cdl/Accordion/Accordion';
import { Button } from '../../cdl/Button/Button';
import { Text } from '../../cdl/Text/Text';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { translate } from '../../common/helpers/translate.helper';
import { getManualOverrides } from '../../common/launchDarkly/getManualOverrides';
import { Box } from '../../common/ui/Box';

import { ArrayFeatureFlagAccordionItem } from './ArrayFeatureFlagAccordionItem';
import { FeatureFlagAccordionItem } from './FeatureFlagAccordionItem';

type FeatureFlagType = boolean | string[] | null;

interface FeatureFlagFormState {
    [key: string]: FeatureFlagType;
}

export const FeatureFlags = () => {
    const allFlags = useFlags();

    const updateFlags = (flags: FeatureFlagFormState) => {
        localStorage.setItem('override-flags', JSON.stringify(flags));
        window.location.reload();
    };

    const resetOverrides = () => {
        localStorage.removeItem('override-flags');
        window.location.reload();
    };

    const overrides = getManualOverrides();

    const initialState: FeatureFlagFormState = Object.keys(allFlags).reduce((acc, flag) => {
        acc[flag] = overrides[flag] ?? null;
        return acc;
    }, {} as FeatureFlagFormState);

    const hasFeatureFlags = Object.keys(allFlags).length > 0;

    const isArrayFlag = (value: FeatureFlagType): value is string[] => {
        return Array.isArray(value);
    };

    const isBooleanFlag = (value: unknown): value is boolean => {
        return typeof value === 'boolean';
    };

    const booleanFlags = Object.keys(allFlags)
        .filter((flagName) => isBooleanFlag(allFlags[flagName]))
        .sort();

    const arrayFlags = Object.keys(allFlags)
        .filter((flagName) => isArrayFlag(allFlags[flagName]))
        .sort();

    return (
        <Formik<FeatureFlagFormState> initialValues={initialState} onSubmit={updateFlags} enableReinitialize>
            <Form>
                <Box display="flex" flexDirection="column" rowGap={4}>
                    <Text variant="subtitle1">{translate('devTools.featureFlags.headline')}</Text>

                    <Text variant="small" color="foreground.muted">
                        {translate('devTools.featureFlags.description')}
                    </Text>
                </Box>

                {hasFeatureFlags ? (
                    <Box marginTop={16}>
                        <Accordion>
                            {[
                                ...booleanFlags.map((flagName) => (
                                    <FeatureFlagAccordionItem
                                        key={flagName}
                                        name={flagName}
                                        launchDarklyValue={allFlags[flagName]}
                                        storedValue={overrides[flagName] ?? null}
                                    />
                                )),
                                ...arrayFlags.map((flagName) => (
                                    <ArrayFeatureFlagAccordionItem
                                        key={flagName}
                                        name={flagName}
                                        launchDarklyValue={allFlags[flagName]}
                                        storedValue={overrides[flagName] ?? null}
                                    />
                                )),
                            ]}
                        </Accordion>
                    </Box>
                ) : (
                    <Text variant="body" as="p">
                        {translate('devTools.featureFlags.empty')}
                    </Text>
                )}

                <Box marginTop={8}>
                    <ButtonGroup>
                        {hasFeatureFlags ? (
                            <Button type="submit">{translate('devTools.featureFlags.save')}</Button>
                        ) : null}
                        <Button type="button" destructive onClick={resetOverrides}>
                            {translate('devTools.featureFlags.reset')}
                        </Button>
                    </ButtonGroup>
                </Box>
            </Form>
        </Formik>
    );
};
